import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
} from '@cartcloser/ui';
export const Route = createFileRoute('/signup')({
  component: SignUpPage,
});

function SignUpPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const response = await fetch('/api/auth/sign-up/email', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-auth-bootstrap-token': setupCode,
      },
      body: JSON.stringify({ name, email, password }),
    });
    setLoading(false);
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { message?: string } | null;
      setError(body?.message ?? 'Account setup failed.');
      return;
    }
    setDone(true);
  }

  return (
    <main className="mx-auto flex max-w-md flex-col gap-6 px-6 py-20">
      <Card>
        <CardHeader>
          <CardTitle>Set up the owner account</CardTitle>
          <CardDescription>This private setup is restricted to the authorised owner.</CardDescription>
        </CardHeader>
        <CardContent>
          {done ? (
            <Alert variant="success">
              <AlertTitle>Account ready</AlertTitle>
              <AlertDescription>
                Your owner account is ready. You can now sign in as {email}.
              </AlertDescription>
            </Alert>
          ) : (
            <form className="space-y-4" onSubmit={onSubmit}>
              {error ? (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
              <FormField label="Name" required>
                {({ id }) => (
                  <Input id={id} value={name} onChange={(e) => setName(e.target.value)} required />
                )}
              </FormField>
              <FormField label="Email" required>
                {({ id }) => (
                  <Input
                    id={id}
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                )}
              </FormField>
              <FormField label="Password" description="At least 8 characters." required>
                {({ id }) => (
                  <Input
                    id={id}
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                )}
              </FormField>
              <FormField
                label="Setup code"
                help="This one-time code is supplied privately during deployment. It prevents anyone else from claiming the owner account."
                required
              >
                {({ id }) => (
                  <Input
                    id={id}
                    type="password"
                    value={setupCode}
                    onChange={(e) => setSetupCode(e.target.value)}
                    required
                  />
                )}
              </FormField>
              <Button type="submit" loading={loading} className="w-full">
                Create account
              </Button>
            </form>
          )}
          <p className="mt-4 text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/signin" className="font-medium text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </main>
  );
}
